import * as React from "react"

import { cva } from "./cva.ts"

/* A component that allows children to overlap each other horizontally. */
export function OverlapGroup({
  children,
  overlapInPixels = 8,
  zIndexPriority = "first",
  initialZIndex = -1,
  className,
}: OverlapGroupProps) {
  const childrenArray = React.Children.toArray(children)
  const childrenArrayLength = childrenArray.length

  return (
    <div
      className={classNameForOverlapGroup({ className })}
      style={overlapGroupStyles(overlapInPixels)}
      role="group"
    >
      {childrenArray.map((child, index) => (
        <span
          tabIndex={-1}
          key={index}
          className={classNameForOverlapGroupItem()}
          style={overlapGroupItemStyles(index, zIndexPriority, initialZIndex, childrenArrayLength)}
        >
          {child}
        </span>
      ))}
    </div>
  )
}

// #region Classnames and Styles

const overlapGroupStyles = (overlapInPixels: number) =>
  ({
    "--overlap-in-pixels": String(overlapInPixels + "px"),
  }) as React.CSSProperties

const overlapGroupItemStyles = (
  index: number,
  zIndexPriority: "first" | "last",
  initialZIndex: number,
  childrenArrayLength: number,
): React.CSSProperties => {
  if (zIndexPriority === "first") {
    return {
      zIndex: initialZIndex - index,
    }
  }

  return {
    zIndex: initialZIndex - childrenArrayLength + index,
  }
}

const classNameForOverlapGroup = cva(["inline-flex"])

const classNameForOverlapGroupItem = cva([
  "relative [&>*]:ring-2 [&>*]:ring-background",
  "[&:not(:first-child)]:-ml-[var(--overlap-in-pixels)]",
])

// #endregion

// #region Types

type OverlapGroupProps = {
  /** The elements to overlap */
  children: React.ReactNode
  /** Amount of overlap between elements in pixels. Defaults to 8. */
  overlapInPixels?: number
  /** Whether the first or last element should appear on top. Defaults to "first". */
  zIndexPriority?: "first" | "last"
  /** The initial z-index value for the items. Useful when the container has a higher z-index. Defaults to -1. */
  initialZIndex?: number
  /** Optional className to apply to the container */
  className?: string
}

// #endregion
